import {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import toast from 'react-hot-toast';
import {InlineWidget, useCalendlyEventListener} from "react-calendly";

const teamSizeData = [
  {
    value:"Small (1-50 employees)",
    label:"Small (1-50 employees)"
  },
  {
    value:"Medium (51-500 employees)",
    label:"Medium (51-500 employees)"
  },
  {
    value:"Large (501-5000 employees)",
    label:"Large (501-5000 employees)"
  },
  {
    value:"Enterprise (5001+ employees)",
    label:"Enterprise (5001+ employees)"
  },
];

const sourceData = [
  {
    value:"Linkedin",
    label:"Linkedin"
  },
  {
    value:"Google Search",
    label:"Google Search"
  },
  {
    value:"Friends & connections",
    label:"Friends & connections"
  },
  {
    value:"Read an article",
    label:"Read an article"
  },
  {
    value:"Other",
    label:"Other"
  }
];

// Calendly color customization - hex colors without #
const CALENDLY_COLORS = {
  background_color: '111928', // Black background to match form
  text_color: 'ffffff',        // White text
  primary_color: '1d64f3',     // Blue-600 for buttons and links
};

// Build Calendly URL with color parameters
const getCalendlyUrl = (baseUrl: string) => {
  const params = new URLSearchParams({
    background_color: CALENDLY_COLORS.background_color,
    text_color: CALENDLY_COLORS.text_color,
    primary_color: CALENDLY_COLORS.primary_color,
  });
  return `${baseUrl}?${params.toString()}`;
};

const JoinWaitListForm = () => {
  const search = useLocation().search;
  const navigate = useNavigate();
  const [payload,setPayload] = useState<any>({})
  const [isSuccess, setIsSuccess] = useState(false)
  const [loading,setLoading] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [currentStep, setCurrentStep] = useState(1) // Step 1: Form, Step 2: Calendar
  const [calendlyKey, setCalendlyKey] = useState(0) // Used to force calendar reset
  const payloadRef = useRef<any>({})
  const ref = useRef<HTMLDivElement>(null);
  const [formWidth, setFormWidth] = useState(630);
  const calendlyUrl = getCalendlyUrl(process.env.REACT_APP_CALENDLY_URL || '');

  useEffect(()=>{
    const email = new URLSearchParams(search).get("email");
    const initialPayload = {email}
    setPayload(initialPayload)
    payloadRef.current = initialPayload
  },[search])

  // Function to reset form to initial state
  const resetForm = () => {
    const email = new URLSearchParams(search).get("email");
    const initialPayload = {email}
    setPayload(initialPayload)
    payloadRef.current = initialPayload
  }

  const isValidEmail = (email: string) => {
    //RFC 2822 standard email validation https://www.w3resource.com/javascript/form/email-validation.php
    const emailReg = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
    return emailReg.test(email)
  }

  // Update ref whenever payload changes
  useEffect(() => {
    payloadRef.current = payload
  }, [payload])

  // Check form validity whenever payload changes
  useEffect(() => {
    const isValid = 
      isValidEmail(payload.email) &&
      payload.name &&
      payload.company_name;
    
    setIsFormValid(!!isValid);
  }, [payload])

  // Set up Calendly event listeners
  useCalendlyEventListener({
    onProfilePageViewed: () => console.log("onProfilePageViewed"),
    onDateAndTimeSelected: async () => {
      console.log("onDateAndTimeSelected - User selected a time slot");
      // When user selects a date/time, submit the form data to API
      if (currentStep === 2) {
        const success = await postData(payloadRef.current);
        if (!success) {
          // Reset the calendar, form fields, and go back to step 1 on error
          setCalendlyKey(prev => prev + 1);
          resetForm();
          setCurrentStep(1);
          window.scrollTo({top: 0, behavior: 'smooth'});
        }
      }
    },
    onEventTypeViewed: () => console.log("onEventTypeViewed"),
    onEventScheduled: (e) => {
      console.log("Event scheduled:", e.data.payload);
      // Show success screen after booking is confirmed
      setIsSuccess(true);
      window.scrollTo({top: 0, behavior: 'smooth'});
    },
    onPageHeightResize: (e) => console.log(e.data.payload.height),
  });

  const handleOnChnage= (e:any)=> {
    const {name,value} = e.target;
    setPayload({...payload,[name]:value})
  }

  const validateForm = () => {
    if(!payload.email) {
      toast.error("Email can not be null/empty!", {duration:2000});
      return false;
    }
    if(!isValidEmail(payload.email)) {
      toast.error("Not a valid email id!", {duration:2000});
      return false;
    }
    if(!payload.name) {
      toast.error("Name can not be null/empty!", {duration:2000});
      return false;
    }
    if(!payload.company_name) {
      toast.error("Company Name can not be null/empty!", {duration:2000});
      return false;
    }
    return true;
  }

  const handleContinueToCalendar = () => {
    if (!validateForm()) return;
    // Move to step 2 to show calendar
    setCurrentStep(2);
  }

  const handleBackToForm = () => {
    setCurrentStep(1);
  }

  useEffect(() => {
    if(ref?.current?.clientWidth) {
      setFormWidth(ref?.current?.clientWidth)
    }
  }, [ref]);

  async function postData(payload:any): Promise<boolean> {
    setLoading(true)
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    try {
      const response = await fetch(`${API_BASE_URL}/demo_request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if(!data.detail) {
        // toast.success(data.message || "Demo request submitted successfully!", {duration:3000});
        setLoading(false)
        return true;
      } else {
        toast.error(data.detail, {duration:4000});
        setLoading(false)
        return false;
      }
    } catch (error) {
      toast.error(`Failed to submit demo request. Please try again.`, {duration:4000});
      setLoading(false)
      return false;
    }
  }

  return (
    <div className="relative w-full min-h-screen bg-black overflow-hidden">
      {/* Ambient Gradient Backgrounds - matching SlackIntegration style */}
      <div className="pointer-events-none absolute -top-6 left-2 w-72 h-48 opacity-40 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute top-16 right-6 w-56 h-40 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 left-6 w-64 h-44 opacity-25 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-72 h-52 opacity-20 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      <div className="pointer-events-none absolute bottom-32 left-1/3 w-56 h-40 opacity-20 bg-gradient-to-tr from-blue-700 via-indigo-800 to-blue-700 rounded-2xl blur-3xl" />
      
      {!isSuccess ? (
        <>
          {/* Header Section */}
          <div className="relative z-10 text-center pt-16 pb-8 px-5 xs:pt-12 xs:pb-6">
            <img 
              src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp" 
              className="mx-auto h-16 mb-8" 
              alt="Cadence Logo" 
            />
            <h1 className="text-white text-5xl xs:text-2xl font-semibold font-['Inter'] mb-4">
              Book a <span className="text-primary-light">Demo</span>
            </h1>
            <p className="text-gray-400 text-lg xs:text-sm font-normal font-['Inter'] max-w-2xl mx-auto">
              See Cadencepro in action. Schedule a personalized demo with our team.
            </p>
            
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-3 mt-8 max-w-md mx-auto">
              {/* Step 1 */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                  currentStep === 1 
                    ? 'bg-primary-light border-primary-light text-white' 
                    : currentStep > 1
                    ? 'bg-green-500 border-green-500 text-white'
                    : 'bg-gray-700 border-gray-600 text-gray-400'
                }`}>
                  {currentStep > 1 ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm font-semibold">1</span>
                  )}
                </div>
                <span className={`text-sm font-medium ${currentStep === 1 ? 'text-white' : 'text-gray-400'}`}>
                  Fill Form
                </span>
              </div>
              
              {/* Divider */}
              <div className={`h-0.5 w-16 xs:w-8 transition-all ${currentStep > 1 ? 'bg-green-500' : 'bg-gray-600'}`} />
              
              {/* Step 2 */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                  currentStep === 2 
                    ? 'bg-primary-light border-primary-light text-white' 
                    : 'bg-gray-700 border-gray-600 text-gray-400'
                }`}>
                  <span className="text-sm font-semibold">2</span>
                </div>
                <span className={`text-sm font-medium ${currentStep === 2 ? 'text-white' : 'text-gray-400'}`}>
                  Select Slot
                </span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="relative z-10 w-full max-w-3xl mx-auto px-6 pb-16 lg:pb-24">
            {/* Step 1: Form */}
            {currentStep === 1 && (
              <div className="w-full" ref={ref}>
                {/* Form Container - matching SignUpForm style */}
                <div className="relative flex-1 w-full">
                  {/* Glow effect */}
                  <div className="pointer-events-none absolute -inset-3 rounded-3xl bg-black/60 blur-2xl" />
                  
                  {/* Form Card */}
                  <div className="relative bg-tertiary-dark border border-gray-700 rounded-2xl p-10 xs:p-6">
                    {/* Loading overlay */}
                    {loading && (
                      <div className="absolute inset-0 z-20 rounded-2xl bg-black/40 backdrop-blur-sm flex items-center justify-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="h-8 w-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span className="text-white text-sm">Submitting...</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-5">
                      {/* Row 1: Name and Email (2 columns on desktop) */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {/* Name Field */}
                        <div>
                          <label htmlFor="name" className="block mb-2 text-sm font-medium text-white">
                            Name <span className="text-red-400">*</span>
                          </label>
                          <input 
                            type="text" 
                            id="name"
                            name="name"
                            className="w-full rounded-lg border border-gray-600 bg-secondary-dark text-white text-sm px-4 py-2.5 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-all"
                            onChange={handleOnChnage}
                            placeholder="John Doe"
                            value={payload?.name || ''}
                          />
                        </div>

                        {/* Email Field */}
                        <div>
                          <label htmlFor="WorkEmail" className="block mb-2 text-sm font-medium text-white">
                            Work Email <span className="text-red-400">*</span>
                          </label>
                          <input 
                            type="email" 
                            id="WorkEmail"
                            name="email"
                            className="w-full rounded-lg border border-gray-600 bg-secondary-dark text-white text-sm px-4 py-2.5 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-all"
                            onChange={handleOnChnage}
                            placeholder="john@company.com"
                            value={payload?.email || ''}
                          />
                        </div>
                      </div>

                      {/* Row 2: Company and Team Size (2 columns on desktop) */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {/* Company Field */}
                        <div>
                          <label htmlFor="company_name" className="block mb-2 text-sm font-medium text-white">
                            Company Name <span className="text-red-400">*</span>
                          </label>
                          <input 
                            type="text" 
                            id="company_name"
                            name="company_name"
                            className="w-full rounded-lg border border-gray-600 bg-secondary-dark text-white text-sm px-4 py-2.5 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-all"
                            onChange={handleOnChnage}
                            placeholder="Acme Inc."
                            value={payload?.company_name || ''}
                          />
                        </div>

                        {/* Team Size Field */}
                        <div>
                          <label htmlFor="team_size" className="block mb-2 text-sm font-medium text-white">
                            Team Size
                          </label>
                          <select 
                            name="team_size" 
                            id="team_size"
                            onChange={handleOnChnage}
                            value={payload?.team_size || ''}
                            className="w-full rounded-lg border border-gray-600 bg-secondary-dark text-white text-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-all"
                          >
                            <option value="">Select team size</option>
                            {teamSizeData.map(({ label, value }) => (
                              <option value={value} key={label} className="bg-tertiary-dark">
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Source Field */}
                      <div>
                        <label htmlFor="source" className="block mb-2 text-sm font-medium text-white">
                          Where did you learn about us?
                        </label>
                        <select 
                          name="source" 
                          id="source"
                          onChange={handleOnChnage}
                          value={payload?.source || ''}
                          className="w-full rounded-lg border border-gray-600 bg-secondary-dark text-white text-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-all"
                        >
                          <option value="">Select source</option>
                          {sourceData.map(({ label, value }) => (
                            <option value={value} key={label} className="bg-tertiary-dark">
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* About Cadence Textarea */}
                      <div>
                        <label htmlFor="about_cadence" className="block mb-2 text-sm font-medium text-white">
                          What excites you about Cadence?
                        </label>
                        <textarea
                          id="about_cadence"
                          name="about_cadence"
                          rows={5}
                          className="w-full rounded-lg border border-gray-600 bg-secondary-dark text-white text-sm px-4 py-2.5 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-transparent transition-all resize-none"
                          onChange={handleOnChnage}
                          placeholder="Tell us what interests you..."
                          value={payload?.about_cadence || ''}
                        />
                      </div>

                      {/* Continue Button */}
                      <div>
                        <button
                          onClick={handleContinueToCalendar}
                          disabled={!isFormValid || loading}
                          className={`w-full px-5 py-3 ${
                            !isFormValid || loading
                              ? "bg-gray-600 cursor-not-allowed" 
                              : "bg-primary-light hover:bg-primary-hover"
                          } rounded-lg text-white text-sm font-medium transition-all flex items-center justify-center gap-2`}
                        >
                          Continue to Calendar
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </button>
                        {!isFormValid && (
                          <p className="text-gray-400 text-xs text-center mt-2">
                            Please fill out all required fields to continue
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Calendar */}
            {currentStep === 2 && (
              <div className="w-full space-y-4">
                {/* Loading overlay - full screen when active */}
                {loading && (
                  <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-12 w-12 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                      <span className="text-white text-lg font-medium">Confirming your booking...</span>
                      <span className="text-gray-400 text-sm">Please wait</span>
                    </div>
                  </div>
                )}
                
                {/* Back Button - Clean and minimal */}
                <button
                  onClick={handleBackToForm}
                  disabled={loading}
                  className="flex items-center gap-2 text-gray-300 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium">Back to Form</span>
                </button>
                
                {/* Instructions - Subtle banner, no heavy box */}
                <div className="flex items-start gap-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-primary-light mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-white text-sm font-medium mb-1">
                      Select Your Preferred Time
                    </p>
                    <p className="text-gray-400 text-xs">
                      Choose a date and time that works best for you. Once you select a slot and confirm, we'll submit your demo request.
                    </p>
                  </div>
                </div>
                
                {/* Calendly Widget - No box, just the calendar */}
                <div className="w-full">
                  <InlineWidget
                    key={calendlyKey}
                    url={calendlyUrl}
                    styles={{
                      width: formWidth,
                      height: 850
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        // Success Screen
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[60vh] px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="relative">
              {/* Glow effect behind success card */}
              <div className="pointer-events-none absolute -inset-8 rounded-3xl bg-green-500/10 blur-3xl" />
              
              <div className="relative bg-tertiary-dark border border-gray-700 rounded-2xl p-12 xs:p-8">
                {/* Logo */}
                <img 
                  src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp" 
                  className="mx-auto h-12 mb-6" 
                  alt="Cadence Logo" 
                />
                
                {/* Success Message */}
                <h2 className="text-white text-3xl xs:text-2xl font-semibold font-['Inter'] mb-4">
                  Demo <span className="text-primary-light">Booked!</span>
                </h2>
                
                <p className="text-gray-300 text-base xs:text-sm font-normal font-['Inter'] mb-3 max-w-md mx-auto">
                  Thanks for scheduling a demo with us!
                </p>
                
                <p className="text-primary-light text-sm font-normal font-['Inter'] mb-8">
                  We can't wait to show you what Cadencepro can do! 🚀
                </p>

                {/* Go to Home Button */}
                <button
                  onClick={() => navigate('/')}
                  className="px-8 py-3 bg-primary-light hover:bg-primary-hover rounded-lg text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JoinWaitListForm;